import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, FileScan, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  therapeutic_areas: 'therapeutic areas',
  preferred_stages: 'stages',
  preferred_modalities: 'modalities',
  geographic_interests: 'geographies',
  partnering_interests: 'partnering interests',
  excluded_therapeutic_areas: 'exclusions',
  disease_areas: 'disease sub-areas',
};

/**
 * "Enrich from Documents" control for pharma company profiles & pharma contacts.
 * Kicks off the background extraction over the entity's corporate deck(s),
 * polls progress, and auto-merges preferences (never overwrites). Renders a
 * compact progress banner while running and calls onDone(summary) when finished.
 */
export const PharmaEnrichButton = ({ entityType, entityId, hasDeck, onDone, className = '' }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);
  const running = job && ['queued', 'running'].includes(job.status);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pharma-enrich/${entityType}/${entityId}/status`, { withCredentials: true });
      setJob(data);
      return data;
    } catch { return null; }
  }, [entityType, entityId]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const loop = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const tick = async () => {
      const j = await poll();
      if (j && ['queued', 'running'].includes(j.status)) {
        pollRef.current = setTimeout(tick, 3000);
      } else if (j && j.status === 'done') {
        const added = j.total_added || 0;
        const ce = j.contacts_enriched || 0;
        const cs = j.contacts_skipped_no_tags || 0;
        const ps = j.people_summary || {};
        const cascade = (ce || cs)
          ? ` · Selective contact enrichment: ${ce} updated, ${cs} untouched (no tags)`
          : '';
        // Aug 2026 · people-resolution summary — the honest post-processing
        // report ("14 people detected · 10 matched · 4 new · 2 ambiguous").
        const peopleLine = (ps.people_detected || 0) > 0
          ? ` · People: ${ps.people_detected} detected — ${ps.matched_existing || 0} matched to existing employees, ${ps.created || 0} new, ${ps.ambiguous || 0} need review · ${ps.preferences_extracted || 0} preferences, ${ps.exclusions || 0} exclusions, ${ps.stage_preferences || 0} stage prefs`
          : '';
        if (added > 0 || ce > 0 || (ps.people_detected || 0) > 0) {
          const parts = Object.entries(j.added_summary || {})
            .map(([k, v]) => `${(v || []).length} ${FIELD_LABELS[k] || k}`);
          toast.success(
            `Enriched: added ${added} company preference(s)` +
            (parts.length ? ` — ${parts.join(', ')}` : '') + cascade + peopleLine,
            { duration: 15000 },
          );
        } else {
          toast.info('Enrichment complete — no new preferences found.' + cascade);
        }
        onDone && onDone(j);
      } else if (j && j.status === 'error') {
        toast.error(j.error || 'Enrichment failed.');
      }
    };
    tick();
  }, [poll, onDone]);

  const start = async () => {
    if (!hasDeck) { toast.error('Upload a corporate deck first, then enrich.'); return; }
    setStarting(true);
    try {
      await axios.post(`${API}/pharma-enrich/${entityType}/${entityId}/run`, {}, { withCredentials: true });
      toast.success('Enriching from documents — this runs in the background.');
      setJob({ status: 'running', total: 1, completed: 0 });
      loop();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start enrichment');
    } finally { setStarting(false); }
  };

  const pct = job && job.total ? Math.round(((job.completed || 0) / job.total) * 100) : 0;

  return (
    <div className={className}>
      <Button
        variant="outline" size="sm"
        onClick={start} disabled={running || starting || !hasDeck}
        className="gap-1.5 text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
        data-testid="pharma-enrich-btn"
        title={hasDeck ? 'Extract BD preferences from the uploaded deck(s)' : 'Upload a corporate deck first'}
      >
        {running || starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileScan className="w-3.5 h-3.5" />}
        {running ? 'Enriching…' : 'Enrich from Documents'}
      </Button>

      {running && (
        <div className="mt-2 border border-violet-200 bg-violet-50/50 rounded-lg p-3" data-testid="pharma-enrich-progress">
          <div className="flex justify-between text-[11px] text-slate-600 mb-1">
            <span>{job.current_document ? `Processing: ${job.current_document}` : 'Starting…'}</span>
            <span>{job.completed || 0} / {job.total || 1}</span>
          </div>
          <div className="h-1.5 bg-violet-100 rounded-full overflow-hidden">
            <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {job && job.status === 'done' && (job.total_added || 0) > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="pharma-enrich-done">
          <CheckCircle2 className="w-3.5 h-3.5" /> Added {job.total_added} preference(s) from documents.
        </div>
      )}
      {job && job.status === 'done' && (job.people_summary?.people_detected || 0) > 0 && (
        <div className="mt-1.5 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-1.5" data-testid="pharma-enrich-people-summary">
          <span className="font-semibold">{job.people_summary.people_detected} people detected</span> · {job.people_summary.matched_existing || 0} matched to existing employees · {job.people_summary.created || 0} new contact{(job.people_summary.created || 0) === 1 ? '' : 's'} · {job.people_summary.preferences_extracted || 0} preferences · {job.people_summary.exclusions || 0} exclusions · {job.people_summary.stage_preferences || 0} stage preferences{(job.people_summary.ambiguous || 0) > 0 ? <span className="font-semibold text-amber-700"> · {job.people_summary.ambiguous} ambiguous — review in Employees</span> : null}
        </div>
      )}
    </div>
  );
};

export default PharmaEnrichButton;
